import { useState } from "react";
import { Link, Redirect, useLocalSearchParams } from "expo-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, FormTitle, InputWithLabel } from "@ptsd/uikit";
import { ArrowLeft } from "@tamagui/lucide-icons";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Form, useMedia, View, XStack } from "tamagui";
import { z } from "zod";

import { authClient } from "~/utils/auth";
import img from "~/assets/signup/signup-3.png";
import {
  AuthBaseForm,
  AuthContainer,
  AuthContent,
  AuthImage,
} from "~/component/feature/auth/wrappers";

export default function ResetPasswordPage() {
  return (
    <AuthContainer>
      <AuthContent>
        <ResetPasswordForm />
      </AuthContent>

      <AuthImage src={img} />
    </AuthContainer>
  );
}

const PasswordError =
  "Пароль повинен складати принаймні 8 символів і містити 1 велику літеру та 1 малу літеру.";
const schema = z
  .object({
    password: z
      .string()
      .min(8, { message: PasswordError })
      .max(32, { message: PasswordError })
      .refine((password) => /[A-Z]/.test(password), {
        message: PasswordError,
      })
      .refine((password) => /[a-z]/.test(password), {
        message: PasswordError,
      }),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Паролі повинні співпадати",
    path: ["confirmPassword"],
  });

export type ResetPasswordFormData = z.infer<typeof schema>;

const ResetPasswordForm = () => {
  const { sm } = useMedia();

  const { token } = useLocalSearchParams<{ token: string }>();
  const { t } = useTranslation("resetpassword");

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const { setError, control, handleSubmit } = useForm<ResetPasswordFormData>({
    defaultValues: { password: "", confirmPassword: "" },
    resolver: zodResolver(schema),
  });

  const onSubmit = (data: z.infer<typeof schema>) => {
    if (isLoading || isSuccess) return;
    setIsSuccess(false);
    setIsLoading(true);
    authClient
      .resetPassword({ newPassword: data.password, token })
      .then((result) => {
        if (result.data?.status === true) setIsSuccess(true);
        if (result.error) setError("password", { message: t(`error`) });
      })
      .catch(() => setError("password", { message: t(`error`) }))
      .finally(() => setIsLoading(false));
  };

  if (!token) return <Redirect href={"/"} />;

  return (
    <AuthBaseForm onSubmit={handleSubmit(onSubmit)}>
      <View width={"100%"} gap={"$5"}>
        {isSuccess && (
          <View gap={"$3"}>
            <View gap={"$2"}>
              <FormTitle>{t("success")}</FormTitle>
            </View>
          </View>
        )}
        {!isSuccess && (
          <>
            <FormTitle>{t("ui.form.title")}</FormTitle>
            <View width={"100%"} gap={"$2"}>
              <View width={"100%"} gap={"$4"}>
                <Controller
                  control={control}
                  name={"password"}
                  rules={{
                    required: true,
                  }}
                  render={({ field, fieldState }) => (
                    <InputWithLabel
                      data-id={"reset-password-input"}
                      secureTextEntry
                      variant="inline"
                      size="md"
                      isError={!!fieldState.error?.message}
                      error={fieldState.error?.message}
                      isMessage
                      label={t("ui.form.password.label")}
                      message={t("ui.form.password.message")}
                      placeholder={t("ui.form.password.placeholder")}
                      onChangeText={(text) => field.onChange(text)}
                      {...field}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name={"confirmPassword"}
                  rules={{
                    required: true,
                  }}
                  render={({ field, fieldState }) => (
                    <InputWithLabel
                      data-id={"reset-password-confirm-input"}
                      secureTextEntry
                      variant="inline"
                      size="md"
                      isMessage
                      isError={!!fieldState.error?.message}
                      error={fieldState.error?.message}
                      label={t("ui.form.confirmPassword.label")}
                      message={t("ui.form.password.message")}
                      placeholder={t("ui.form.confirmPassword.placeholder")}
                      onChangeText={(text) => field.onChange(text)}
                      {...field}
                    />
                  )}
                />
              </View>
            </View>
          </>
        )}
      </View>

      <XStack gap={"$0.5"}>
        <Link data-id={"reset-password-back"} href="/auth" asChild>
          <Button
            variant="gray"
            size={sm ? "md" : "lg"}
            mode="icon"
            rounded="lg"
          >
            <ArrowLeft color={"$gray-primary-900"} size={"$2.5"} />
          </Button>
        </Link>

        {!isSuccess && (
          <Form.Trigger asChild>
            <Button
              data-id={"reset-password-submit"}
              flex={1}
              variant="primary"
              size={sm ? "md" : "lg"}
              rounded="lg"
            >
              {isLoading
                ? t("ui.form.actions.submit.loading")
                : t("ui.form.actions.submit.label")}
            </Button>
          </Form.Trigger>
        )}
      </XStack>
    </AuthBaseForm>
  );
};
